import { metaResponseSchema } from "@portfolio-demos/demo-core";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from "react";
import type { ZodType } from "zod";
import { AdminApp, AdminLogin } from "./admin/AdminApp";
import {
  byProvinceResponseSchema,
  childrenResponseSchema,
  contestsResponseSchema,
  type ElectionResult,
  electionResultsResponseSchema,
  fetchJson,
  type RegionSummary,
  regionDetailSchema,
  regionsSchema,
  searchResponseSchema,
  statsSchema,
} from "./api";
import { RankedBarChart } from "./components/RankedBarChart";
import { type RegionLeader, RegionSchematic } from "./components/RegionSchematic";
import { DemoResetNotice } from "./reset";

const SENATE_CONTEST = "00399000";
const PARTY_LIST_CONTEST = "01199000";

/* One reserved card per region in the demonstration scope, which the fixtures fix at four. */
const LOCAL_CARD_SLOTS = ["one", "two", "three", "four"] as const;

/*
 * The local grid's own geometry, held before the regions arrive. Empty cards carry the reserve in CSS,
 * so this stays in step with the real grid's columns, gaps, and card height at every width.
 */
const LocalCardsPlaceholder = () => (
  <div className="local-results-grid" aria-hidden="true">
    {LOCAL_CARD_SLOTS.map((slot) => (
      <div className="local-results-card" key={slot} />
    ))}
  </div>
);

const publicNavigation = [
  { href: "/", lines: ["Your", "Results"] },
  { href: "/senate", lines: ["Your", "Senators"] },
  { href: "/party-list", lines: ["Your", "Party-list"] },
  { href: "/local", lines: ["Your", "Local results"] },
] as const;

export type ApiState<Data> = {
  data: Data | null;
  error: string | null;
  loading: boolean;
};

type ApiRequestState<Data> = ApiState<Data> & {
  requestUrl: string | null;
};

export const resolveApiStateForUrl = <Data,>(
  state: ApiRequestState<Data>,
  url: string | null,
  keepPreviousData = false,
): ApiState<Data> =>
  state.requestUrl === url
    ? state
    : {
        data: keepPreviousData ? state.data : null,
        error: null,
        loading: Boolean(url),
      };

export const getApiGroupStatus = (
  states: readonly ApiState<unknown>[],
): "pending" | "error" | "ready" => {
  if (states.some((state) => state.error)) {
    return "error";
  }
  if (states.some((state) => state.data === null)) {
    return "pending";
  }
  return "ready";
};

const useApi = <Data,>(
  url: string | null,
  schema: ZodType<Data>,
  keepPreviousData = false,
): ApiState<Data> => {
  const [state, setState] = useState<ApiRequestState<Data>>({
    requestUrl: url,
    data: null,
    error: null,
    loading: Boolean(url),
  });

  useEffect(() => {
    if (!url) {
      setState({ requestUrl: null, data: null, error: null, loading: false });
      return;
    }

    const controller = new AbortController();
    let active = true;
    setState((current) => ({
      requestUrl: url,
      data: keepPreviousData ? current.data : null,
      error: null,
      loading: true,
    }));
    void fetchJson(url, schema, controller.signal)
      .then((data) => {
        if (active) {
          setState({ requestUrl: url, data, error: null, loading: false });
        }
      })
      .catch((error: unknown) => {
        if (!active || (error instanceof DOMException && error.name === "AbortError")) {
          return;
        }
        setState({
          requestUrl: url,
          data: null,
          error: error instanceof Error ? error.message : "The request could not be completed.",
          loading: false,
        });
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [keepPreviousData, schema, url]);

  return resolveApiStateForUrl(state, url, keepPreviousData);
};

const formatNumber = (value: number) => value.toLocaleString("en-PH");

const formatTimestamp = (value: string | undefined) => {
  if (!value) {
    return "Awaiting results batch";
  }
  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Manila",
    timeZoneName: "short",
  }).format(new Date(value));
};

const pathWithoutTrailingSlash = () => {
  const pathname = window.location.pathname;
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
};

const readAppLocation = () => ({
  pathname: pathWithoutTrailingSlash(),
  search: window.location.search,
});

const safeDecode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const makeLocalPath = (parts: readonly string[]) =>
  `/local/${parts.map((part) => encodeURIComponent(part)).join("/")}`;

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

const ElectionLockup = ({ footer = false }: { footer?: boolean }) => (
  <span className={footer ? "election-lockup election-lockup--footer" : "election-lockup"}>
    <span>
      Election <strong>2025</strong>
    </span>
    <span>Results</span>
    <small>National count desk</small>
  </span>
);

const SummaryCard = ({
  title,
  percentage,
  current,
  total,
  label,
}: {
  title: string;
  percentage: string | undefined;
  current: number | undefined;
  total: number | undefined;
  label: string;
}) => {
  const boundedPercent = Math.max(0, Math.min(Number(percentage ?? 0), 100));
  const progressStyle = {
    "--summary-progress": `${boundedPercent}%`,
  } as CSSProperties;

  return (
    <article className="summary-card">
      <div className="summary-card__heading">
        <span>{title}</span>
        <strong>{percentage ? `${percentage}%` : "—"}</strong>
      </div>
      <div
        className="summary-card__track"
        role="progressbar"
        aria-label={title}
        aria-valuemin={0}
        aria-valuemax={100}
        {...(percentage ? { "aria-valuenow": boundedPercent } : {})}
      >
        <span style={progressStyle} />
      </div>
      <p>
        {current === undefined || total === undefined ? (
          "Loading result totals…"
        ) : (
          <>
            <strong>{formatNumber(current)}</strong> of <strong>{formatNumber(total)}</strong>{" "}
            {label}
          </>
        )}
      </p>
    </article>
  );
};

const SiteShell = ({ children, pathname }: { children: ReactNode; pathname: string }) => {
  const meta = useApi("/api/meta", metaResponseSchema);
  const stats = useApi("/api/stats", statsSchema);
  const [menuState, setMenuState] = useState({ pathname, isOpen: false });
  const isMenuOpen = menuState.pathname === pathname && menuState.isOpen;
  const isResultsRoute = pathname === "/" || pathname === "/senate" || pathname === "/party-list";
  const reportingCopy = stats.data
    ? `${stats.data.precinctsProcessedPercentage}% of reporting units are in`
    : "Reporting data is loading";
  const hasImportedResults = stats.data?.synthetic === false;

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="masthead">
        <div className="masthead__inner">
          <a className="wordmark" href="/" aria-label="Election results demo home">
            <ElectionLockup />
          </a>
          <button
            className="menu-button"
            type="button"
            aria-expanded={isMenuOpen}
            aria-controls="public-navigation"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            onClick={() =>
              setMenuState((current) => ({
                pathname,
                isOpen: current.pathname === pathname ? !current.isOpen : true,
              }))
            }
          >
            <span />
            <span />
            <span />
          </button>
          <div
            className={isMenuOpen ? "masthead__actions is-open" : "masthead__actions"}
            id="public-navigation"
          >
            <nav className="primary-nav" aria-label="Primary navigation">
              <ul>
                {publicNavigation.map((item) => {
                  const current =
                    item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
                  return (
                    <li key={item.href}>
                      <a href={item.href} aria-current={current ? "page" : undefined}>
                        <span>{item.lines[0]}</span>
                        <strong>{item.lines[1]}</strong>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </nav>
            <search>
              <form className="masthead-search" action="/search" method="get">
                <button type="submit" aria-label="Search">
                  <SearchIcon />
                </button>
                <label className="sr-only" htmlFor="site-search">
                  Search the demo
                </label>
                <input
                  id="site-search"
                  name="search"
                  type="search"
                  maxLength={80}
                  placeholder="Search a name or place"
                />
              </form>
            </search>
          </div>
        </div>
      </header>

      <section className="reporting-summary" aria-label="Reporting summary">
        <article className="reporting-summary__notice">
          <div className="reporting-summary__notice-title">
            <span className="info-icon" aria-hidden="true">
              i
            </span>
            {isResultsRoute ? (
              <h1>
                {hasImportedResults ? "Partial imported results" : "Partial demonstration results"}
                <span className="sr-only"> — {reportingCopy}</span>
              </h1>
            ) : (
              <h2>
                {hasImportedResults ? "Partial imported results" : "Partial demonstration results"}
              </h2>
            )}
          </div>
          <div className="reporting-summary__source">
            <p>
              <strong>Updated:</strong> {formatTimestamp(stats.data?.lastUpdated)}
            </p>
            <p>
              <strong>Source:</strong>{" "}
              {hasImportedResults
                ? "uploaded production result feed"
                : "deterministic synthetic dataset"}
            </p>
          </div>
          <p>
            {hasImportedResults
              ? "Imported April 30 production snapshot; geographic demo panels remain illustrative"
              : "Synthetic Philippine election example — not official election data"}
          </p>
        </article>
        <SummaryCard
          title="Total reports processed"
          percentage={stats.data?.precinctsProcessedPercentage}
          current={stats.data?.precinctsProcessed}
          total={stats.data?.totalPrecincts}
          label="reporting units"
        />
        <SummaryCard
          title="Votes in"
          percentage={stats.data?.voterTurnoutPercentage}
          current={stats.data?.totalVotes}
          total={stats.data?.registeredVoters}
          label="registered voters"
        />
      </section>

      <DemoResetNotice />
      <main id="main-content">{children}</main>

      <footer className="site-footer">
        <a href="/" aria-label="Election results demo home">
          <ElectionLockup footer />
        </a>
        <nav aria-label="Footer navigation">
          {publicNavigation.map((item) => (
            <a href={item.href} key={item.href}>
              <span>{item.lines[0]}</span>
              <strong>{item.lines[1]}</strong>
            </a>
          ))}
          <a href="/login">
            <span>Results</span>
            <strong>Back office</strong>
          </a>
        </nav>
        <p>
          {hasImportedResults ? "Imported national snapshot" : "Synthetic data only"}
          <span>
            {meta.error
              ? "API status unavailable"
              : hasImportedResults
                ? "Local panels remain synthetic · no analytics"
                : "No live feeds · no analytics"}
          </span>
          <a href="https://jericop.dev">Built by Jerico Pulvera</a>
        </p>
      </footer>
    </div>
  );
};

const ErrorBand = ({ message }: { message: string }) => (
  <div className="error-band" role="alert">
    <strong>Desk update unavailable.</strong>
    <span>{message}</span>
  </div>
);

const LoadingBand = () => (
  <div className="loading-band" role="status">
    <span />
    Reading the result count…
  </div>
);

const createRegionLeaders = (
  rows: readonly (readonly [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    number,
    number,
    number,
    number,
    number,
    number,
    string,
  ])[],
  results: readonly ElectionResult[],
): Record<string, RegionLeader> => {
  const candidateLookup = new Map(results.map((result) => [result.candidateCode, result]));
  const regionCandidates = new Map<string, Map<string, number>>();
  const regionTotals = new Map<string, number>();

  for (const row of rows) {
    const regionName = row[0];
    const candidateCode = row[15];
    const votes = row[9];
    const candidates = regionCandidates.get(regionName) ?? new Map<string, number>();
    candidates.set(candidateCode, (candidates.get(candidateCode) ?? 0) + votes);
    regionCandidates.set(regionName, candidates);
    regionTotals.set(regionName, (regionTotals.get(regionName) ?? 0) + votes);
  }

  return Object.fromEntries(
    [...regionCandidates.entries()].flatMap(([regionName, candidateVotes]) => {
      const leader = [...candidateVotes.entries()].sort((left, right) => right[1] - left[1])[0];
      if (!leader) {
        return [];
      }
      const candidate = candidateLookup.get(leader[0]);
      if (!candidate) {
        return [];
      }
      const totalVotes = regionTotals.get(regionName) ?? 0;
      return [
        [
          regionName,
          {
            candidateCode: candidate.candidateCode,
            candidateName: candidate.candidateName,
            partyAlias: candidate.partyAlias,
            colorSlot: candidate.colorSlot,
            votesAmount: leader[1],
            votePercentage: totalVotes === 0 ? 0 : Math.round((leader[1] / totalVotes) * 1000) / 10,
          },
        ],
      ];
    }),
  );
};

const LocalResultsGrid = ({ regions }: { regions: readonly RegionSummary[] }) => (
  <section className="local-results-grid" aria-label="Selected Philippine regions">
    {regions.map((region) => (
      <article className="local-results-card" key={region.code}>
        <header>
          <a href={makeLocalPath([region.name])}>{region.displayName}</a>
        </header>
        <div className="local-results-card__columns" aria-hidden="true">
          <span>Location</span>
          <span>Percent</span>
          <span>Precincts</span>
        </div>
        <div className="local-results-card__rows">
          {region.provinces.map((province, index) => {
            const progressStyle = {
              "--local-progress": `${Math.max(0, Math.min(province.percent, 100))}%`,
            } as CSSProperties;
            return (
              <a
                className="local-result-row"
                href={makeLocalPath([region.name, province.name])}
                key={province.name}
                style={progressStyle}
              >
                <span className="local-result-row__rank">{index + 1}</span>
                <span className="local-result-row__data">
                  <strong>{province.name}</strong>
                  <strong>{province.percent.toFixed(1)}%</strong>
                  <strong>{province.precincts}</strong>
                </span>
              </a>
            );
          })}
        </div>
        <footer>
          <span>{region.reporting.percent.toFixed(1)}% region reporting</span>
          <a href={makeLocalPath([region.name])}>View all</a>
        </footer>
      </article>
    ))}
  </section>
);

const DashboardPage = ({ initialContestCode }: { initialContestCode: string }) => {
  const [contestSelection, setContestSelection] = useState({
    routeContestCode: initialContestCode,
    selectedContestCode: initialContestCode,
  });
  const contestCode =
    contestSelection.routeContestCode === initialContestCode
      ? contestSelection.selectedContestCode
      : initialContestCode;
  const [selectedRegion, setSelectedRegion] = useState<string | null>(
    new URLSearchParams(window.location.search).get("region"),
  );
  const regions = useApi("/api/local/regions", regionsSchema);
  const nationalResults = useApi(
    `/api/election-results?contestCode=${encodeURIComponent(contestCode)}`,
    electionResultsResponseSchema,
    true,
  );
  const scopeQuery = new URLSearchParams({ contestCode });
  if (selectedRegion) {
    scopeQuery.set("regionName", selectedRegion);
  }
  const scopedResults = useApi(
    `/api/election-results?${scopeQuery.toString()}`,
    electionResultsResponseSchema,
    true,
  );
  const byProvince = useApi(
    `/api/election-results/by-province?contestCode=${encodeURIComponent(contestCode)}`,
    byProvinceResponseSchema,
    true,
  );

  const regionLeaders = useMemo(
    () =>
      byProvince.data && nationalResults.data
        ? createRegionLeaders(byProvince.data.results, nationalResults.data.results)
        : {},
    [byProvince.data, nationalResults.data],
  );
  const selectedRegionData = regions.data?.find((region) => region.name === selectedRegion);
  const currentResults = scopedResults.data?.results ?? nationalResults.data?.results ?? [];
  const isPartyList = contestCode === PARTY_LIST_CONTEST;
  const hasImportedNationalResults = nationalResults.data?.synthetic === false;
  const dashboardRequests = [regions, nationalResults, scopedResults, byProvince] as const;
  const dashboardStatus = getApiGroupStatus(dashboardRequests);
  const error = dashboardRequests.find((request) => request.error)?.error ?? null;

  const selectRegion = (regionName: string) => {
    setSelectedRegion((current) => (current === regionName ? null : regionName));
  };

  return (
    <div className="page-frame">
      {dashboardStatus === "error" && error ? <ErrorBand message={error} /> : null}

      {/*
        The pending frame reserves the geometry the ready page will occupy — the control row, the
        scope status line, the map-and-leads board, the ranking panel, and the local section's four
        cards. Every piece left out moves the footer up during the count and drops it back on arrival:
        with the local section missing, that was a 660px jump at 1440.
      */}
      {dashboardStatus === "pending" ? (
        <>
          <section className="election-workspace" aria-label="National election results">
            <div className="map-column">
              <div className="map-column__controls">
                <label className="scope-selector">
                  <span>Reporting scope</span>
                  <select disabled aria-busy="true">
                    <option value="">All demo regions</option>
                  </select>
                </label>
              </div>
              <p className="scope-status" />
              <div className="map-panel" aria-hidden="true">
                <div className="philippines-map-art-box" />
                <div className="region-leads" />
              </div>
            </div>
            <div className="results-panel">
              <LoadingBand />
            </div>
          </section>

          <section className="local-results-section" aria-hidden="true">
            <h2>Your local results</h2>
            <LocalCardsPlaceholder />
          </section>
        </>
      ) : null}

      {dashboardStatus === "ready" &&
      regions.data &&
      nationalResults.data &&
      scopedResults.data &&
      byProvince.data ? (
        <>
          <section className="election-workspace" aria-label="National election results">
            <div className="map-column">
              <div className="map-column__controls">
                <label className="scope-selector">
                  <span>Reporting scope</span>
                  <select
                    value={selectedRegion ?? ""}
                    onChange={(event) => setSelectedRegion(event.currentTarget.value || null)}
                  >
                    <option value="">All demo regions</option>
                    {regions.data.map((region) => (
                      <option key={region.code} value={region.name}>
                        {region.displayName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <p className="scope-status" aria-live="polite">
                {selectedRegionData
                  ? hasImportedNationalResults
                    ? `${selectedRegionData.displayName} is selected. This regional view is an illustrative proportional breakdown of the imported national feed.`
                    : `${selectedRegionData.displayName} is selected. The place name is real; every measure, ranking, and total below is synthetic.`
                  : hasImportedNationalResults
                    ? "National rankings use the active imported feed; the four-region map remains an illustrative demo view."
                    : "Four representative regions are available in this synthetic Philippine view."}
              </p>

              <RegionSchematic
                regions={regions.data}
                leaders={regionLeaders}
                legendResults={nationalResults.data.results}
                selectedRegion={selectedRegion}
                onSelectRegion={selectRegion}
              />
            </div>

            <div className="results-panel">
              <div className="results-panel__controls">
                <span>{selectedRegionData?.displayName ?? "Philippines"}</span>
                <fieldset className="result-tabs">
                  <legend className="sr-only">National contest</legend>
                  <button
                    type="button"
                    aria-pressed={!isPartyList}
                    onClick={() =>
                      setContestSelection({
                        routeContestCode: initialContestCode,
                        selectedContestCode: SENATE_CONTEST,
                      })
                    }
                  >
                    Your senator
                  </button>
                  <button
                    type="button"
                    aria-pressed={isPartyList}
                    onClick={() =>
                      setContestSelection({
                        routeContestCode: initialContestCode,
                        selectedContestCode: PARTY_LIST_CONTEST,
                      })
                    }
                  >
                    Your party-list
                  </button>
                </fieldset>
              </div>

              <RankedBarChart
                title={isPartyList ? "Party-list ranking" : "Senate ranking"}
                description={
                  hasImportedNationalResults
                    ? selectedRegionData
                      ? `Illustrative regional distribution derived from the active imported national results for ${selectedRegionData.displayName}.`
                      : "Exact national totals from the active imported production results."
                    : `Exact synthetic results for ${selectedRegionData?.displayName ?? "the four-region Philippine demonstration scope"}.`
                }
                results={currentResults}
                isRefreshing={scopedResults.loading && Boolean(scopedResults.data)}
                withPortraits={!isPartyList && nationalResults.data.synthetic}
              />
            </div>
          </section>

          <section className="local-results-section" aria-labelledby="local-results-title">
            <h2 id="local-results-title">Your local results</h2>
            <LocalResultsGrid regions={regions.data} />
          </section>
        </>
      ) : null}
    </div>
  );
};

const SearchPage = ({ searchString }: { searchString: string }) => {
  const term = new URLSearchParams(searchString).get("search")?.trim() ?? "";
  const search = useApi(
    term ? `/api/search?search=${encodeURIComponent(term)}` : null,
    searchResponseSchema,
  );

  return (
    <div className="page-frame page-frame--narrow">
      <section className="section-hero">
        <p className="story-kicker">Search desk</p>
        <h1>{term ? `Matches for “${term}”` : "Search the synthetic count"}</h1>
        <p>
          Find fictional candidates and party-list groups alongside selected real Philippine place
          names. Search never touches an account or production index.
        </p>
      </section>
      <search>
        <form className="search-deck" action="/search" method="get">
          <label htmlFor="page-search">Search term</label>
          <div>
            <input
              key={term}
              id="page-search"
              name="search"
              type="search"
              defaultValue={term}
              maxLength={80}
            />
            <button type="submit">Run search</button>
          </div>
        </form>
      </search>
      {search.error ? <ErrorBand message={search.error} /> : null}
      {search.loading && !search.data ? <LoadingBand /> : null}
      <section className="search-results" aria-live="polite">
        {!term ? <p>Enter a name or Philippine place to begin.</p> : null}
        {term && search.data?.results.length === 0 ? <p>No synthetic matches found.</p> : null}
        {search.data?.results.map((result) => (
          <article key={`${result.type}-${result.value}-${result.candidateContestName}`}>
            <div>
              <span>{result.type}</span>
              <h2>{result.value}</h2>
              <p>
                {result.candidateContestName ||
                  [result.region, result.province, result.city].filter(Boolean).join(" · ")}
              </p>
            </div>
            {result.link ? <a href={result.link}>Open result →</a> : null}
          </article>
        ))}
      </section>
    </div>
  );
};

const LocalIndexPage = () => {
  const regions = useApi("/api/local/regions", regionsSchema);

  return (
    <div className="page-frame page-frame--local">
      <h1 className="page-title-band">Local results</h1>
      {regions.error ? <ErrorBand message={regions.error} /> : null}
      {/*
        The reserved grid stands in for a loading band here: a band swapped for four cards moved the
        footer by 488px on a narrow screen. The status stays announced without occupying any geometry.
      */}
      {regions.loading && !regions.data ? (
        <p className="sr-only" role="status">
          Reading the synthetic count…
        </p>
      ) : null}
      {regions.data ? <LocalResultsGrid regions={regions.data} /> : <LocalCardsPlaceholder />}
    </div>
  );
};

const LocalContestResult = ({
  contestCode,
  contestName,
  regionName,
  provinceName,
  municipalityName,
}: {
  contestCode: string;
  contestName: string;
  regionName: string;
  provinceName?: string;
  municipalityName?: string;
}) => {
  const query = new URLSearchParams({ contestCode, regionName });
  if (provinceName) {
    query.set("provinceName", provinceName);
  }
  if (municipalityName) {
    query.set("municipalityName", municipalityName);
  }
  const result = useApi(`/api/election-results?${query.toString()}`, electionResultsResponseSchema);
  if (result.error) {
    return <ErrorBand message={result.error} />;
  }
  if (!result.data) {
    return <LoadingBand />;
  }
  return (
    <RankedBarChart
      title={contestName}
      description="A synthetic Philippine election example. Ranking is by demonstration votes; no official result or projection is shown."
      results={result.data.results}
      isRefreshing={result.loading}
      withPortraits={contestCode === SENATE_CONTEST}
      visibleTitle
    />
  );
};

const LocalDrilldownPage = ({ segments }: { segments: readonly string[] }) => {
  const [regionName, provinceName, municipalityName] = segments;
  const scopeQuery = new URLSearchParams({ regionName: regionName ?? "" });
  if (provinceName) {
    scopeQuery.set("provinceName", provinceName);
  }
  if (municipalityName) {
    scopeQuery.set("municipalityName", municipalityName);
  }
  const region = useApi(
    regionName ? `/api/local/region?regionName=${encodeURIComponent(regionName)}` : null,
    regionDetailSchema,
  );
  const children = useApi(
    regionName && !municipalityName ? `/api/local/children?${scopeQuery.toString()}` : null,
    childrenResponseSchema,
  );
  const contests = useApi(
    regionName ? `/api/local/contests?${scopeQuery.toString()}` : null,
    contestsResponseSchema,
  );
  const regionLabel = region.data?.displayName ?? regionName;
  const heading = municipalityName ?? provinceName ?? regionLabel;
  const isRegionLevel = !provinceName && !municipalityName;
  const error = region.error ?? children.error ?? contests.error;
  const childLabel = provinceName ? "Cities / municipalities" : "Provinces";

  return (
    <div className="page-frame">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <ol>
          <li>
            <a href="/local">Local desk</a>
          </li>
          {regionName ? (
            <li aria-current={provinceName ? undefined : "page"}>
              {provinceName ? <a href={makeLocalPath([regionName])}>{regionLabel}</a> : regionLabel}
            </li>
          ) : null}
          {provinceName ? (
            <li aria-current={municipalityName ? undefined : "page"}>
              {municipalityName ? (
                <a href={makeLocalPath([regionName ?? "", provinceName])}>{provinceName}</a>
              ) : (
                provinceName
              )}
            </li>
          ) : null}
          {municipalityName ? <li aria-current="page">{municipalityName}</li> : null}
        </ol>
      </nav>

      <section className="section-hero section-hero--split">
        <div>
          <p className="story-kicker">Local result</p>
          {/*
            At region level the heading upgrades from the URL segment to the fetched display name,
            which is longer, so it reserves the two lines that name will need.
          */}
          <h1 className={isRegionLevel ? "section-hero__heading--reserved" : undefined}>
            {heading}
          </h1>
        </div>
        {/*
          Only the first sentence waits on the fetch, and its placeholder is written to the same length,
          so the paragraph keeps its line count — and the page its height — when the count arrives.
        */}
        <p>
          {region.data
            ? `${region.data.reporting.processed} of ${region.data.reporting.total} regional reporting units are in.`
            : "Reading the regional reporting count."}{" "}
          Location names are real; all totals and results are fictional.
        </p>
      </section>

      {error ? <ErrorBand message={error} /> : null}

      {/* Heading and container are route-derived, so they render before the list arrives. */}
      {!municipalityName ? (
        <section className="child-locations" aria-labelledby="child-location-title">
          <div className="section-heading">
            <div>
              <p className="section-index">Next level</p>
              <h2 id="child-location-title">{childLabel}</h2>
            </div>
            <p>Select a location to continue the drilldown.</p>
          </div>
          <div>
            {children.data?.children.map((child, index) => (
              <a
                key={child}
                href={makeLocalPath([
                  regionName ?? "",
                  ...(provinceName ? [provinceName] : []),
                  child,
                ])}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{child}</strong>
                <span>Open →</span>
              </a>
            ))}
          </div>
        </section>
      ) : null}

      {/*
        The scope is route-derived, so the contest reserve below can be exact before the fetch: the
        fixtures rank six candidates at region level and three under it.
      */}
      <section
        className="local-contests"
        aria-labelledby="local-contests-title"
        data-scope={isRegionLevel ? "region" : "local"}
      >
        <div className="section-heading">
          <div>
            <p className="section-index">Contest board</p>
            <h2 id="local-contests-title">Synthetic local contest</h2>
          </div>
          <p>No race calls, write-ins, or editorial controls.</p>
        </div>
        {/* Pending state sits where the panels land, so the swap does not move the page. */}
        {contests.loading && !contests.data ? <LoadingBand /> : null}
        {contests.data?.contests.length === 0 ? <p>No contests found for this scope.</p> : null}
        {contests.data?.contests.map((contest) => (
          <LocalContestResult
            key={contest.contestCode}
            contestCode={contest.contestCode}
            contestName={contest.contestName}
            regionName={regionName ?? ""}
            {...(provinceName ? { provinceName } : {})}
            {...(municipalityName ? { municipalityName } : {})}
          />
        ))}
      </section>
    </div>
  );
};

const NotFoundPage = () => (
  <div className="page-frame page-frame--narrow">
    <section className="not-found">
      <p className="story-kicker">404 · Route not found</p>
      <h1>This desk does not publish that route.</h1>
      <p>
        The public results, back office, and supported downloads are available from their main
        navigation routes.
      </p>
      <a href="/">Return to the overview →</a>
    </section>
  </div>
);

const RoutedPage = ({ pathname, search }: { pathname: string; search: string }) => {
  if (pathname === "/") {
    return <DashboardPage initialContestCode={SENATE_CONTEST} />;
  }
  if (pathname === "/senate") {
    return <DashboardPage initialContestCode={SENATE_CONTEST} />;
  }
  if (pathname === "/party-list") {
    return <DashboardPage initialContestCode={PARTY_LIST_CONTEST} />;
  }
  if (pathname === "/search") {
    return <SearchPage searchString={search} />;
  }
  if (pathname === "/local") {
    return <LocalIndexPage />;
  }
  if (pathname.startsWith("/local/")) {
    const segments = pathname.slice("/local/".length).split("/").map(safeDecode);
    if (segments.length >= 1 && segments.length <= 3 && segments.every(Boolean)) {
      return <LocalDrilldownPage segments={segments} />;
    }
  }
  return <NotFoundPage />;
};

export const App = () => {
  const [location, setLocation] = useState(readAppLocation);

  useEffect(() => {
    const syncLocation = () => setLocation(readAppLocation());
    const commitNavigation = (url: URL) => {
      window.history.pushState(null, "", `${url.pathname}${url.search}${url.hash}`);
      syncLocation();
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };
    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest("a[href]");
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.hasAttribute("download") ||
        (anchor.target && anchor.target !== "_self")
      ) {
        return;
      }
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) {
        return;
      }
      if (
        url.pathname === window.location.pathname &&
        url.search === window.location.search &&
        url.hash
      ) {
        return;
      }
      event.preventDefault();
      commitNavigation(url);
    };
    const handleSubmit = (event: SubmitEvent) => {
      if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) {
        return;
      }
      const form = event.target;
      if (form.method.toLowerCase() !== "get" || (form.target && form.target !== "_self")) {
        return;
      }
      const url = new URL(form.action, window.location.href);
      if (url.origin !== window.location.origin) {
        return;
      }
      url.search = "";
      for (const [name, value] of new FormData(form)) {
        if (typeof value === "string") {
          url.searchParams.append(name, value);
        }
      }
      event.preventDefault();
      commitNavigation(url);
    };

    window.addEventListener("popstate", syncLocation);
    document.addEventListener("click", handleClick);
    document.addEventListener("submit", handleSubmit);
    return () => {
      window.removeEventListener("popstate", syncLocation);
      document.removeEventListener("click", handleClick);
      document.removeEventListener("submit", handleSubmit);
    };
  }, []);

  if (location.pathname === "/login") {
    return <AdminLogin />;
  }
  if (
    location.pathname === "/cms" ||
    location.pathname.startsWith("/cms/") ||
    location.pathname === "/admin" ||
    location.pathname.startsWith("/admin/")
  ) {
    return <AdminApp pathname={location.pathname.replace(/^\/admin/, "/cms")} />;
  }

  return (
    <SiteShell pathname={location.pathname}>
      <RoutedPage pathname={location.pathname} search={location.search} />
    </SiteShell>
  );
};
